import { Link } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";

const SHIP = 5.99;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;
  const total = subtotal + (items.length ? SHIP : 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} data-testid="cart-backdrop" />
      <aside className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#8b6f3d]" />
            <h3 className="font-serif-mystery text-xl">{T.title}</h3>
            <span className="text-xs font-mono-data text-zinc-500">({items.length})</span>
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-close-btn" className="p-2 rounded-full hover:bg-zinc-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-zinc-500" data-testid="cart-empty">
              <ShoppingBag size={40} className="mx-auto mb-4 text-zinc-300" />
              <p>{T.empty}</p>
            </div>
          ) : items.map(it => (
            <div key={`${it.product_id}-${it.size}`} className="flex gap-3 border-b border-zinc-100 pb-4"
              data-testid={`cart-item-${it.product_id}-${it.size}`}>
              <img src={it.image} alt={it.name[lang]} className="w-20 h-20 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-zinc-900 leading-tight mb-1">{it.name[lang]}</div>
                <div className="text-xs text-zinc-500 mb-2">{T.size}: <strong>{it.size}</strong></div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 border border-zinc-300 rounded-full">
                    <button data-testid={`qty-dec-${it.product_id}-${it.size}`}
                      onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center hover:bg-zinc-100 rounded-full"><Minus size={12} /></button>
                    <span className="w-7 text-center text-sm font-mono-data" data-testid={`qty-${it.product_id}-${it.size}`}>{it.quantity}</span>
                    <button data-testid={`qty-inc-${it.product_id}-${it.size}`}
                      onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center hover:bg-zinc-100 rounded-full"><Plus size={12} /></button>
                  </div>
                  <div className="font-medium text-sm text-[#8b6f3d]">{(it.price * it.quantity).toFixed(2)} CAD</div>
                </div>
              </div>
              <button data-testid={`cart-remove-${it.product_id}-${it.size}`} onClick={() => removeItem(it.product_id, it.size)}
                className="text-zinc-400 hover:text-red-500 h-fit p-1"><X size={14} /></button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-zinc-200 p-5 space-y-3 bg-zinc-50">
            <div className="flex justify-between text-sm text-zinc-600">
              <span>{T.subtotal}</span>
              <span data-testid="cart-subtotal">{subtotal.toFixed(2)} CAD</span>
            </div>
            <div className="flex justify-between text-sm text-zinc-600">
              <span>{T.shipping}</span>
              <span>{SHIP.toFixed(2)} CAD</span>
            </div>
            <div className="flex justify-between text-lg font-medium border-t border-zinc-200 pt-3">
              <span>Total</span>
              <span className="text-[#8b6f3d]" data-testid="cart-total">{total.toFixed(2)} CAD</span>
            </div>
            <Link to="/checkout" onClick={() => setOpen(false)}>
              <Button data-testid="cart-checkout-btn" className="w-full bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 text-base font-medium">
                {T.checkout}
              </Button>
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}

const FR = {
  title: "Panier",
  empty: "Votre panier est vide.",
  size: "Taille",
  subtotal: "Sous-total",
  shipping: "Livraison",
  checkout: "Passer commande",
};
const EN = {
  title: "Cart",
  empty: "Your cart is empty.",
  size: "Size",
  subtotal: "Subtotal",
  shipping: "Shipping",
  checkout: "Proceed to checkout",
};
